import { hospitalityGroup } from "@mmh/mmh"

export function generateAccessibilityStatement() {
    const { name, dba, email, phone } = hospitalityGroup.parentCompany
    
    const sections = [
        {
            title: `Accessibility Statement for ${name}`,
            content: [
                `At ${dba}, under the ownership of ${name}, we are dedicated to ensuring equal access to our services, both online and in person. We are committed to providing an inclusive and accessible experience for all users, including individuals with disabilities, and we continuously strive to meet or exceed recognized accessibility standards.`
            ]
        },
        {
            title: "General Accessibility Commitment",
            content: [
                "We recognize the diverse needs of individuals with disabilities and are committed to accommodating all visitors to our website and restaurant. Guided by the Web Content Accessibility Guidelines (WCAG) 2.1 Level A and AA, we assess and update our digital environment regularly to improve usability and accessibility for everyone. Accessibility is an ongoing process, and we welcome feedback to identify and address potential barriers."
            ]
        },
        {
            title: "Ongoing Improvements and Maintenance",
            content: [
                "We continuously review and enhance our website to align with current accessibility standards and best practices.",
                [
                    {
                        title: "Accessibility Testing",
                        content: "We perform both automated and user-based testing, including input from individuals with disabilities, to guide improvements."
                    },
                    {
                        title: "Employee Training",
                        content: "Team members responsible for website content and functionality receive ongoing training on WCAG guidelines and best practices for accessible web design."
                    }
                ]
            ]
        },
        {
            title: "Accessibility Features",
            content: [
                [
                    {
                        title: "Multimedia Accessibility",
                        content: "All videos on our site include captions or transcripts to ensure equal access to information."
                    },
                    {
                        title: "Browser Compatibility",
                        content: "We support commonly used screen readers, browser accessibility settings, and third-party assistive technologies. We also provide guides on using these tools to improve the experience for all visitors."
                    }
                ]
            ]
        },
        {
            title: "Third-Party Content and Links",
            content: [
                "While we strive to ensure third-party content (e.g., maps, reservation systems, or social media plugins) meets accessibility standards, we acknowledge that we do not control these elements. If you encounter difficulties with such content, please contact us for assistance.",
                "Links to external websites are provided for convenience. However, we have not reviewed their accessibility features and cannot guarantee compliance with accessibility standards."
            ]
        },
        {
            title: "Physical Accessibility",
            content: [
                `We are equally committed to ensuring accessibility at our physical location. ${name} offers:`,
                [
                    { 
                        title: "", 
                        content: "Accessible entrances, seating areas, and restrooms."
                    },
                    {
                        title: "", 
                        content: "Accommodations for guests with specific needs."
                    }
                ],
                "Please contact us in advance to ensure we can provide any necessary assistance for your visit."
            ]
        },
        {
            title: "Privacy and Anonymity",
            content: [
                "When providing accessibility feedback or requesting assistance, you are not required to disclose sensitive personal information, such as the nature of your disability, unless you choose to do so."
            ]
        },
        {
            title: "Reporting and Assistance",
            content: [
                "We value your feedback and welcome reports of accessibility-related concerns. If you experience difficulty using our website or require assistance, please contact us at any of the following email addresses or phone numbers:",
                [
                    {
                        title: "Email",
                        content: email
                    }
                ],
                [
                    {
                        title: "Accessibility Hotline",
                        content: phone
                    }
                ],
                "Our team will promptly review your concerns and work to address any issues."
            ]
        },
        {
            title: "Legal Disclaimer",
            content: [
                "While we are committed to providing an accessible experience, some content or features may still present challenges for certain users. We are actively working to resolve these and appreciate your patience as we continuously enhance accessibility."
            ]
        },
        {
            title: "Commitment to Continuous Improvement",
            content: [
                `Accessibility is a journey, and ${name} is dedicated to making ongoing improvements to our digital and physical spaces. Your feedback is invaluable to us in this process, and we thank you for helping us create a more inclusive environment.`
            ]
        }
    ]

    return sections.map((section, sectionIndex) => {
        const sectionNumber = `${sectionIndex + 1}. `
        const contentHTML = section.content.map(item => {
            if (typeof item === "string") {
                return `<p class="mt-2">${item}</p>`
            } else if (Array.isArray(item)) {
                const listItems = item.map(listItem => {
                    if (listItem.title.length) {
                        return `<li><strong>${listItem.title}:</strong> ${listItem.content}</li>`
                    } else {
                        return `<li>${listItem.content}</li>`
                    }
                }).join("")
                return `<ul class="list-disc pl-6 mt-2">${listItems}</ul>`
            }
            return ""
        }).join("")
        
        return `<div class="mb-6"><h2 class="font-semibold text-lg mb-1">${sectionNumber}${section.title}</h2>${contentHTML}</div>`
    }).join("")
}